import { useEffect, useRef, useState } from "react";
import ReportLayout from "../../../../components/dashboard/service/reports/ReportLayout";
import PMSteps from "../../../../components/utils/PMSteps";
import { useTranslation } from "react-i18next";
import { useUserHasAccess } from "../../../../routes/roleOnly";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  OperationCanFinish,
  dispatchWithToast,
  stripHtml,
  yupHtmlString,
} from "../../../../components/Utils";
import { useDispatch, useSelector } from "react-redux";
import {
  createReportAsync,
  updateReportAsync,
} from "../../../../slices/service/reports";
import { customerLockBlocksSubmit } from "../../../../contexts/CustomerLockTracker";
import { ReportReportForm } from "../../../../components/dashboard/service/reports/forms/report";
import { CustomerSystemForm } from "../../../../components/dashboard/customers/systems/customer-system";
import OrderItems from "../../../../components/dashboard/orders/order-items";
import { ReportVariables } from "../../../../components/dashboard/service/reports/report-variables";
import ChecklistItemLines from "../../../../components/dashboard/quality/checklists/checklistItemLines";
import { PMNotice } from "../../../../components/utils/notifications";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { PMField } from "../../../../components/utils/PMField";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TWclassNames } from "../../../../components/utils/Div";
import { reloadResources } from "i18next";
import ChecklistItemLinesCompact from "../../../../components/dashboard/quality/checklists/checklistItemLinesCompact";
import { promangToast } from "../../../../operationFeedback";
import { runFinishReportOperation } from "../../../../components/dashboard/service/reports/runFinishReportOperation";

export const ReportFinishPageV2 = () => {
  const Layout = (props) => {
    const { onRefresh } = props;


    const [report, setReport] = useState(props.report);

    const canManage = useUserHasAccess({ role: "report", action: "manage" });

    // Update report state if props.report change
    useEffect(() => {
      setReport(props.report);
    }, [props.report]);

    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.session?.currentCompany);
    /** When true, navigate back only after save/finish confirms (FE-RP-02). */
    const navigateAfterSaveRef = useRef(false);

    const [isLoading, setIsLoading] = useState(false);

    const { t } = useTranslation();
    const adminRole = useUserHasAccess({ role: "admin" });
    const [currentStageIdx, setCurrentStageIdx] = useState(0);

    // set isLoading depending on props
    useEffect(() => {
      setIsLoading(props.isLoading);
    }, [props.isLoading]);

    const [stages, setStages] = useState([
      {
        id: 1,
        title: t("Rapport"),
        description: t("report.write.description"),
        current: true,
        completed: false,
        active: true,
      },
      {
        id: 2,
        title: t("System"),
        description: t("report.write.description"),
        active: report?.report_type?.system ? true : false,
      },
      {
        id: 3,
        title: t("Items"),
        description: t("report.items.description"),
        current: false,
        completed: false,
        active: report?.report_type?.items ? true : false,
      },
      {
        id: 4,
        title: t("Variables"),
        description: t("report.variables.description"),
        current: false,
        completed: false,
        active: report?.report_type?.variables ? true : false,
      },
      {
        id: 5,
        title: t("Checklist"),
        description: t("report.checklist.description"),
        current: false,
        completed: false,
        active: report?.report_type?.checklist ? true : false,
      },
      {
        id: 6,
        title: t("Finish"),
        description: t("report.finish.description"),
        current: false,
        completed: false,
        active: adminRole,
      },
    ]);

    const activeStages = stages.filter((stage) => stage.active === true);

    const [stage, setStage] = useState(stages[0]);

    // get stage from url params

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
      const stage = searchParams.get("stage");
      if (stage) {
        setStage(stages[stage]);
        setCurrentStageIdx(stage);
      }
    }, [searchParams]);

    const activePageChecker = (stage) => {
      // Check if active page, else set next page
      if (stage.active) {
        const thisStageId = stage.id;
        const currentStageIdx = activeStages.findIndex(
          (stage) => stage.id === thisStageId
        );
        setStage((prevState) => stage);
        setCurrentStageIdx(currentStageIdx);
      } else {
        // create a loop that goes to next stage until it finds an active stage
        let nextStage = stage.id;
        while (nextStage < activeStages.length) {
          if (activeStages[nextStage].active) {
            setStage((prevState) => activeStages[nextStage - 1]);
            // idx of current "nextStage"
            const nextStageIdx = activeStages.findIndex(
              (stage) => stage.id === nextStage
            );
            setCurrentStageIdx(nextStage - 1);
            break;
          } else {
            nextStage++;
          }
        }
      }
    };

    const handleStageChange = (stage) => {
      setIsLoading(true);

      setTimeout(() => {
        setIsLoading(false);
      }, 250);

      activePageChecker(stage);
    };

    const handleNextPage = async (formik) => {
      await formik.validateForm();

      // tell Formik to touch all fields so errors are visible
      formik.setTouched(
        Object.keys(formik.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
        true
      );

      if (!formik.isValid) {
        promangToast.error(
          "Skjemaet inneholder feil – rett opp før du går videre."
        );
        return;
      }

      // Bytt steg etter validering er godkjent
      if (currentStageIdx !== activeStages?.length - 1) {
        const nextStage = activeStages[currentStageIdx + 1];
        activePageChecker(nextStage);
      }

      formik.submitForm();
    };

    const createOrUpdateDispatch = (data) => {
      if (report?.id) {
        const res = dispatch(updateReportAsync(data));
        return res;
      } else {
        const res = dispatch(createReportAsync(data));
        return res;
      }
    };

    const [refresh, setRefresh] = useState(false);

    const handleOnRefresh = (data) => {
      // setItemState((prevState) => ({
      //   ...prevState,
      //   report: data,
      // }));

      setReport(data);

      if (props.onRefresh && refresh) {
        onRefresh(data || data?.data);
      }
    };

    const navigate = useNavigate();

    const navigateBackToReport = (values, payload) => {
      const id = payload?.id || payload?.data?.id || values?.id;
      if (id) {
        navigate(`/dashboard/service/reports/${id}`, {
          state: {
            message: values?.finished
              ? t("operation_feedback.finish_report.success")
              : t("Saved"),
            report: payload?.data || payload || values,
          },
        });
        return;
      }
      navigate("/dashboard/service/reports/");
    };

    const handleFinish = (formik) => {
      // Do not navigate until the async save/finish operation confirms.
      navigateAfterSaveRef.current = true;
      setRefresh(true);
      formik.submitForm();
    };

    function FinishErrors() {
      // Return errors with type "create_order" in report.operations
      if (report?.operations) {
        const errors = report.operations.filter(
          (operation) => operation.name === "finish_report"
        );
        return (
          <div className="col-span-6 sm:col-span-6">
            <div className="text-red-500">
              {errors.map((error, index) => (
                <div key={index}>{error.message}</div>
              ))}
            </div>
          </div>
        );
      }
    }

    const stageOne = (report, formik) => {
      return <ReportReportForm formik={formik} report={report} />;
    };

    const stageSystem = (report, formik) => {
      return (
        <CustomerSystemForm
          selected={report?.system}
          formik={formik}
          customer={report?.customer}
          onAttachSystem={(data) => {
            formik.setFieldValue("system_id", data.target.value);
          }}
        />
      );
    };

    const stageTwo = (report, formik) => {
      return (
        <div className="grid grid-cols-6 gap-6 mt-10">
          <div className="col-span-6 sm:col-span-6">
            <OrderItems
              group={false}
              sales_price={true}
              purchase_price={false}
              discount={true}
              surcharge={false}
              drag={false}
              quantity_picked={false}
              quantity={true}
              quote_line={true}
              {...formik}
            />
          </div>
        </div>
      );
    };

    const stageThree = (report, formik) => {
      return (
        <div className="grid grid-cols-6 gap-6 mt-10">
          <div className="col-span-6 sm:col-span-6">
            <ReportVariables {...formik} />
          </div>
        </div>
      );
    };

    const stageFour = (report, formik) => {
      return (
        <div className="grid grid-cols-6 gap-6 mt-10">
          <div className="col-span-6">
            {report?.checklist?.id ? (
              <ChecklistItemLinesCompact
                checklist={report?.checklist}
                items={report?.checklist?.items}
                editable={false}
                isLoading={isLoading}
                drag={false}
                addRow={false}
                deleteRow={false}
                deviation={false}
                disabled={
                  report?.checklist?.is_items_editable !== undefined &&
                  !report?.checklist?.is_items_editable
                }
                // parentCallback={(data) =>
                //   // filter out items that are missing product or description
                //   formik.setFieldValue(
                //     "checklist.items",
                //     data.filter(
                //       (item) =>
                //         item.product_id !== null && item.description !== null
                //     )
                //   )
                // }
                parentCallback={(data) =>
                  // filter out items that are missing product or description
                  formik.setFieldValue("checklist.items", data)
                }
              />
            ) : (
              <PMNotice
                title="Sjekkliste mangler"
                description="Rapporten er ikke knyttet til sjekkliste"
              />
            )}
          </div>
        </div>
      );
    };

    const stageSix = (report, formik) => {
      const canFinish = OperationCanFinish({
        operations: report?.operations,
        operationName: "finish_report",
      });

      return (
        <div className="grid grid-cols-6 gap-6 mt-10">
          {canFinish && (
            <div className="col-span-6 sm:col-span-6 border-2 border-red-200 rounded-xl p-5">
              <FinishErrors />
            </div>
          )}

          <div className="col-span-6 sm:col-span-6 border-2 border-green-200 rounded-xl p-5">
            <p className="font-semibold mb-2">Ferdigstill rapporten</p>
            <div className="py-5">
              <PMSwitch name="finished" label={t("Ferdigstill")} {...formik} />
            </div>
            <PMField
              name="finished_date"
              label={t("Finished Date")}
              type="date"
              {...formik}
            />
            {formik.values.finished && (
              <p>Rapporten ferdigstilles ved oppdatering.</p>
            )}
          </div>
        </div>
      );
    };

    const getInitialValues = () => {
      const base = { id: report?.id };

      switch (stage?.id) {
        case 1:
          return {
            ...base,
            report: report?.report,
            internal_report: report?.internal_report,
          };
        case 2:
          return { ...base, system_id: report?.system_id };
        case 3:
          return {
            ...base,
            items: (report?.items || []).filter((item) =>
              canManage ? true : !item.is_quote_line
            ),
          };
        case 4:
          return { ...base, variables: report?.variables };
        case 5:
          return { ...base, checklist: report?.checklist };

        case 6:
          return {
            ...base,
            finished: report?.finished || false,
            finished_date: report?.finished_date?.substring(0, 10),
          };
        default:
          return base;
      }
    };

    const validationSchemas = {
      1: Yup.object({
        report: yupHtmlString("Rapporttekst mangler"),
      }),
      2: Yup.object({
        system_id: Yup.string().required("Velg et system"),
      }),
      //4: Yup.object({
      //  variables: Yup.object().required("Variabler mangler"),
      //}),
      5: Yup.object({
        checklist: Yup.object({
          items: Yup.array().of(
            Yup.object({
              isDeviation: Yup.boolean(),
              description: Yup.string()
                .nullable()
                .when("isDeviation", {
                  is: true,
                  then: (schema) =>
                    schema.required("Beskrivelse er påkrevd for avvik"),
                  otherwise: (schema) => schema.notRequired(),
                }),
            })
          ),
        }),
      }),

      6: Yup.object({
        finished: Yup.boolean().oneOf([true], "Du må ferdigstille rapporten"),
        finished_date: Yup.string().required("Dato må velges"),
      }),
    };

    return (
      <div className="container bg-white px-4 py-4 rounded-xl">
        <div>
          <PMSteps
            steps={activeStages}
            selected={stage}
            onChange={handleStageChange}
          />
        </div>
        <div>
          {isLoading ? (
            <ResourceLoading />
          ) : (
            <Formik
              initialValues={getInitialValues()}
              validationSchema={validationSchemas[stage?.id] || Yup.object()}
              onSubmit={async (values, helpers) => {
                const shouldNavigate = navigateAfterSaveRef.current;
                navigateAfterSaveRef.current = false;
                try {
                  setIsLoading(true);

                  // FE-RP-02: finish_report is dialog-owned — no toast-per-subcall,
                  // and never navigate before the operation confirms.
                  if (values.finished === true) {
                    const result = await runFinishReportOperation({
                      dispatch,
                      updateReportAsync,
                      values,
                      report,
                      companyId: currentCompany?.id,
                      onRefresh: async (data) => {
                        handleOnRefresh(data);
                      },
                      t,
                    });
                    if (result.ok) {
                      helpers.setStatus({ success: true });
                      if (shouldNavigate) {
                        navigateBackToReport(values, result.payload);
                      }
                    } else {
                      helpers.setStatus({ success: false });
                      if (result.error?.safeMessage) {
                        helpers.setErrors({
                          submit: result.error.safeMessage,
                        });
                      }
                    }
                    return;
                  }

                  await dispatchWithToast(
                    createOrUpdateDispatch(values),
                    (data) => {
                      handleOnRefresh(data);
                      if (shouldNavigate) {
                        navigateBackToReport(values, data);
                      }
                    },
                    () => setIsLoading(false)
                  );

                  helpers.setStatus({ success: true });
                } catch (err) {
                  promangToast.error(
                    err?.message || t("operation_feedback.generic_error")
                  );
                  console.error(err);
                  helpers.setStatus({ success: false });
                  helpers.setErrors({ submit: err.message });
                } finally {
                  setIsLoading(false);
                  helpers.setSubmitting(false);
                }
              }}
            >
              {(formik) => (
                <Form>
                  {stage?.id === 1 && stageOne(report, formik)}
                  {stage?.id === 2 && stageSystem(report, formik)}
                  {stage?.id === 3 && stageTwo(report, formik)}
                  {stage?.id === 4 && stageThree(report, formik)}
                  {stage?.id === 5 && stageFour(report, formik)}
                  {stage?.id === 6 && stageSix(report, formik)}

                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-3 sm:gap-3 justify-between">
                    {formik.errors.submit && (
                      <div className="text-red-500 mt-2">
                        {formik.errors.submit}
                      </div>
                    )}

                    {Object.keys(formik.errors).length > 0 &&
                      formik.submitCount > 0 && (
                        <div className="text-red-500 mt-4">
                          <p>
                            Skjemaet inneholder feil – vennligst rett opp før du
                            går videre.
                          </p>
                          {/* Error */}
                          {Object.keys(formik.errors).map((key, index) => (
                            <div key={index}>
                              {key}: {formik.errors[key]}
                            </div>
                          ))}
                        </div>
                      )}

                    <div
                      className={TWclassNames(
                        "flex w-full col-span-3 right align-right gap-4",
                        currentStageIdx === activeStages?.length - 1
                          ? "justify-end"
                          : "justify-between"
                      )}
                    >
                      <button
                        // disabled={
                        //   currentStageIdx === activeStages?.length - 1 &&
                        //   !OperationCanFinish({
                        //     operations: report?.operations,
                        //     operationName: "finish_report",
                        //   })
                        // }
                        type="button"
                        disabled={customerLockBlocksSubmit(formik.values, {
                          originalCustomerId: report?.id
                            ? report.customer_id
                            : null,
                        })}
                        className="disabled:bg-gray-200 right-0 rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                        onClick={() => handleFinish(formik)}
                      >
                        {report?.id ? t("Update") : t("Create")}
                        {" & Back to Report"}
                      </button>
                      {currentStageIdx !== activeStages?.length - 1 && (
                        <button
                          disabled={isLoading}
                          type="button"
                          className="rounded-md border border-transparent disabled:bg-gray-500 disabled:cursor-wait bg-green-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                          onClick={() => handleNextPage(formik)}
                        >
                          {t("Next")} &raquo;
                        </button>
                      )}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <ReportLayout>
        <Layout />
      </ReportLayout>
    </>
  );
};
